import {
    AlertTriangle,
    ArrowRight,
    CheckCircle2,
    ChevronLeft,
    DatabaseZap,
    LoaderCircle,
    RefreshCcw,
    Sparkles,
    XCircle,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './RegenerateEmbeddings.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const RegenerateEmbeddings = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [progress, setProgress] = useState(null)
    const [isRunning, setIsRunning] = useState(false)

    const runBatch = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Please select an active store first.')
            return
        }

        try {
            setIsRunning(true)
            const response = await fetch(BASE_URL_API + 'payKbRegenerateEmbeddings', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    batchSize: 10,
                    forceAll: false,
                }),
            })
            const respo = await response.json()

            if (respo?.status === 'success') {
                setProgress(respo?.response || null)
                toast.success(respo?.message || 'Embedding batch completed.')
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to regenerate embeddings.')
        } catch {
            toast.error('Network error while processing embedding batch.')
        } finally {
            setIsRunning(false)
        }
    }

    const summaryText = useMemo(() => {
        if (!progress) return 'No batch has been processed yet.'
        if (progress?.hasMore) {
            return `Last batch: ${Number(progress?.succeeded || 0)}/${Number(progress?.processed || 0)} succeeded · ${Number(progress?.remaining || 0)} remaining.`
        }
        return `Last batch: ${Number(progress?.succeeded || 0)}/${Number(progress?.processed || 0)} succeeded · All done!`
    }, [progress])

    const showStats = Boolean(progress)

    return (
        <div className={styles.page}>
            <header className={styles.topBar}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={16} />
                </button>
                <div className={styles.titleWrap}>
                    <span className={styles.topPill}>
                        <Sparkles size={13} /> Knowledge Bank Admin
                    </span>
                    <h1>Regenerate Embeddings</h1>
                </div>
            </header>

            <section className={styles.heroCard}>
                <div className={styles.heroIconWrap}>
                    <DatabaseZap size={18} />
                </div>
                <div className={styles.heroTextWrap}>
                    <h2>Embedding Processor</h2>
                    <p>
                        Processes the next 10 KB articles that need fresh vector embeddings for smarter answer ranking and retrieval.
                    </p>
                </div>
            </section>

            <section className={styles.controlCard}>
                <div className={styles.metricRow}>
                    <span className={styles.metricLabel}>Articles needing embedding</span>
                    <strong className={styles.metricValue}>{progress ? Number(progress?.remaining || 0) : '...'}</strong>
                </div>

                <button type="button" className={styles.runBtn} onClick={runBatch} disabled={isRunning}>
                    {isRunning ? <LoaderCircle size={14} className={styles.spinIcon} /> : <RefreshCcw size={14} />}
                    {isRunning ? 'Processing...' : 'Process Next 10'}
                    {isRunning ? null : <ArrowRight size={13} />}
                </button>

                <p className={styles.summaryText}>{summaryText}</p>

                {showStats ? (
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <span>Processed</span>
                            <strong>{Number(progress?.processed || 0)}</strong>
                        </div>
                        <div className={styles.statCard}>
                            <span>Succeeded</span>
                            <strong>{Number(progress?.succeeded || 0)}</strong>
                        </div>
                        <div className={styles.statCard}>
                            <span>Failed</span>
                            <strong>{Number(progress?.failed || 0)}</strong>
                        </div>
                        <div className={styles.statCard}>
                            <span>Batch Size</span>
                            <strong>{Number(progress?.batchSize || 10)}</strong>
                        </div>
                    </div>
                ) : null}

                {showStats ? (
                    <div className={styles.statusRow}>
                        {Number(progress?.failed || 0) > 0 ? (
                            <span className={`${styles.statusChip} ${styles.statusChipWarn}`}>
                                <AlertTriangle size={13} /> {Number(progress?.failed || 0)} failed
                            </span>
                        ) : (
                            <span className={`${styles.statusChip} ${styles.statusChipSuccess}`}>
                                <CheckCircle2 size={13} /> All processed entries succeeded
                            </span>
                        )}

                        {progress?.hasMore ? (
                            <span className={`${styles.statusChip} ${styles.statusChipInfo}`}>
                                <RefreshCcw size={13} /> {Number(progress?.remaining || 0)} remaining
                            </span>
                        ) : (
                            <span className={`${styles.statusChip} ${styles.statusChipDone}`}>
                                <CheckCircle2 size={13} /> Queue complete
                            </span>
                        )}
                    </div>
                ) : null}

                {showStats && Array.isArray(progress?.failedIds) && progress.failedIds.length > 0 ? (
                    <div className={styles.failedBox}>
                        <strong><XCircle size={14} /> Failed Article IDs</strong>
                        <ul>
                            {progress.failedIds.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                {showStats ? (
                    <p className={styles.modelVersionText}>Model Version: {progress?.modelVersion || 'N/A'}</p>
                ) : null}
            </section>
        </div>
    )
}

export default RegenerateEmbeddings